// UnifiedDetailedCashFlow.tsx
import React, { useState, useEffect, forwardRef } from 'react';
import { ChevronDownSquare, ChevronUpSquare } from 'lucide-react';
import { AccountEntry, CategoryTemplate, Project, ViewType } from '../../types';
import { ViewToggle } from '../ui/ViewToggle';
import { getAllFinancialReports, compareMonthYear as compareMonthYearHelper } from '../../lib/data/financialReportsHelper';
import { getDateForPeriod } from '../../lib/utils/dateMapping';
import { FrontendCFCalculationService } from '../../lib/financial/frontendCFCalculations';

interface UnifiedDetailedCashFlowProps {
  accounts: AccountEntry[];
  activeTemplate: CategoryTemplate | null;
  selectedProject?: Project | null;
}

// ViewType now imported from types.ts

interface CFLineItem {
  key: string;
  name: string;
  values: (number | null)[];
}

interface CFSection {
  key: string;
  name: string;
  line_items: CFLineItem[];
}

interface CFDetailData {
  periods: string[];
  sections: CFSection[];
}

export const UnifiedDetailedCashFlow = forwardRef<HTMLDivElement, UnifiedDetailedCashFlowProps>(({ 
  accounts, 
  activeTemplate, 
  selectedProject 
}, ref) => {
  const [viewType, setViewType] = useState<ViewType>('monthly');
  const [expanded, setExpanded] = useState<{ [section: string]: boolean }>({});
  const [cfDetailData, setCFDetailData] = useState<CFDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingExpandAll, setPendingExpandAll] = useState(false);
  const [showOpeningBalancesModal, setShowOpeningBalancesModal] = useState(false);

  // Frontend calculation mode - use frontend calculations by default
  const [calculationSource, setCalculationSource] = useState<'frontend' | 'backend'>('frontend');

  // Generate frontend CF data
  const frontendCFData = React.useMemo(() => {
    if (calculationSource === 'frontend' && accounts.length > 0 && activeTemplate) {
      try {
        return FrontendCFCalculationService.buildCFData(accounts, activeTemplate, viewType, selectedProject);
      } catch (err) {
        console.error('Error generating frontend CF data:', err);
        return null;
      }
    }
    return null;
  }, [calculationSource, accounts, activeTemplate, viewType, selectedProject]);

  // Get data source based on calculation mode
  const currentDataSource = calculationSource === 'frontend' && frontendCFData
    ? frontendCFData
    : cfDetailData;
  
  // Detection for missing opening balance data
  const hasMissingOpeningBalances = (): boolean => {
    if (!currentDataSource || currentDataSource.periods.length === 0) return false;
    
    // Check if this looks like the first period of available data
    // This is indicated by the first month having zero or very small beginning cash
    // and/or working capital changes that seem to represent opening balances rather than changes
    const firstPeriod = currentDataSource.periods[0];
    const cashRollSection = currentDataSource.sections.find(s => s.key === 'cash_roll_forward');
    const beginningCashItem = cashRollSection?.line_items.find(item => item.key === 'beginning_cash');
    
    if (beginningCashItem && beginningCashItem.values.length > 0) {
      const firstBeginningCash = beginningCashItem.values[0];
      // If beginning cash is 0 or null, likely missing opening data
      return firstBeginningCash === 0 || firstBeginningCash === null;
    }
    
    return false;
  };

  // Check if current view has partial period data
  const hasPartialPeriod = (): boolean => {
    if (!hasMissingOpeningBalances() || !currentDataSource) return false;
    
    // For quarterly/yearly views, check if the aggregated period includes the first incomplete month
    if (viewType === 'quarterly' || viewType === 'yearly') {
      const transformedData = transformDataForView(currentDataSource);
      if (!transformedData) return false;
      
      // Check if any target period includes the first month with missing opening data
      const firstMonth = currentDataSource.periods[0];
      return transformedData.periods.some(period => {
        // This is a simplified check - in reality we'd need to map periods to months
        // For now, assume if we have missing opening data, quarterly/yearly views are partial
        return true;
      });
    }
    
    return false;
  };

  // Apply N/A handling for missing opening balance data
  const applyNAHandling = (data: CFDetailData): CFDetailData => {
    if (!hasMissingOpeningBalances()) return data;
    
    const modifiedData = JSON.parse(JSON.stringify(data)); // Deep clone
    
    // Items that should show N/A when opening balances are missing
    const naItems = [
      // Working capital changes
      'change_in_trade_receivables',
      'change_in_inventory', 
      'change_in_other_current_assets',
      'change_in_trade_payables',
      'change_in_other_current_liabilities',
      // Investing activities (balance sheet changes)
      'capex_ppe', // Capital Expenditures - calculated from PPE changes
      'asset_sales_dispositions', // Asset sales - calculated from asset changes
      // Financing activities (balance sheet changes)
      'debt_raise_paydown', // Debt changes - calculated from debt balance changes
      'equity_raise_repurchase', // Equity changes - calculated from equity balance changes
      'other_financing_activities', // Other financing - often balance sheet related
      // Note: beginning_cash, net_change_in_cash, ending_cash_rollforward will be calculated
      // Note: section_total will be calculated (not N/A)
      'check_ending_cash_vs_balance_sheet' // This stays N/A since it's a check
    ];
    
    // First pass: Set N/A items
    modifiedData.sections.forEach(section => {
      section.line_items.forEach(item => {
        if (naItems.includes(item.key)) {
          // Set first period value to null to trigger N/A display
          if (item.values.length > 0) {
            item.values[0] = null;
          }
        }
      });
    });
    
    // Second pass: Calculate cash roll-forward items in correct order
    const cashRollSection = modifiedData.sections.find(s => s.key === 'cash_roll_forward');
    if (cashRollSection) {
      // 1. Calculate Net Change in Cash = sum of all section totals
      const netChangeItem = cashRollSection.line_items.find(li => li.key === 'net_change_in_cash');
      if (netChangeItem && netChangeItem.values.length > 0) {
        let netChange = 0;
        modifiedData.sections.forEach(sect => {
          if (sect.key !== 'cash_roll_forward') { // Exclude cash roll-forward section
            const sectionTotal = sect.line_items.find(li => li.key === 'section_total');
            if (sectionTotal && sectionTotal.values.length > 0) {
              const sectionValue = sectionTotal.values[0];
              if (sectionValue !== null && sectionValue !== undefined) {
                netChange += sectionValue;
              }
            }
          }
        });
        netChangeItem.values[0] = netChange;
      }
      
      // 2. Calculate Beginning Cash = Cash on Balance Sheet - Net Change in Cash
      const beginningCashItem = cashRollSection.line_items.find(li => li.key === 'beginning_cash');
      const cashOnBSItem = cashRollSection.line_items.find(li => li.key === 'cash_on_balance_sheet');
      if (beginningCashItem && netChangeItem && cashOnBSItem && beginningCashItem.values.length > 0) {
        const cashOnBS = cashOnBSItem.values[0];
        const netChange = netChangeItem.values[0];
        
        if (cashOnBS !== null && netChange !== null) {
          beginningCashItem.values[0] = cashOnBS - netChange;
        }
      }
      
      // 3. Calculate Ending Cash (Roll-Forward) = Beginning Cash + Net Change in Cash
      const endingCashItem = cashRollSection.line_items.find(li => li.key === 'ending_cash_rollforward');
      if (endingCashItem && beginningCashItem && netChangeItem && endingCashItem.values.length > 0) {
        const beginningCash = beginningCashItem.values[0];
        const netChange = netChangeItem.values[0];
        
        if (beginningCash !== null && netChange !== null) {
          endingCashItem.values[0] = beginningCash + netChange;
        }
      }
    }
    
    return modifiedData;
  };

  // Apply cash roll-forward calculations to any transformed data (quarterly/yearly)
  const applyCashCalculations = (data: CFDetailData): CFDetailData => {
    const modifiedData = JSON.parse(JSON.stringify(data)); // Deep clone
    
    // Apply to each period in the transformed data
    const cashRollSection = modifiedData.sections.find(s => s.key === 'cash_roll_forward');
    if (cashRollSection) {
      for (let periodIdx = 0; periodIdx < modifiedData.periods.length; periodIdx++) {
        // 1. Calculate Net Change in Cash = sum of all section totals for this period
        const netChangeItem = cashRollSection.line_items.find(li => li.key === 'net_change_in_cash');
        if (netChangeItem && netChangeItem.values.length > periodIdx) {
          let netChange = 0;
          modifiedData.sections.forEach(sect => {
            if (sect.key !== 'cash_roll_forward') { // Exclude cash roll-forward section
              const sectionTotal = sect.line_items.find(li => li.key === 'section_total');
              if (sectionTotal && sectionTotal.values.length > periodIdx) {
                const sectionValue = sectionTotal.values[periodIdx];
                if (sectionValue !== null && sectionValue !== undefined) {
                  netChange += sectionValue;
                }
              }
            }
          });
          netChangeItem.values[periodIdx] = netChange;
        }
        
        // 2. Calculate Beginning Cash = Cash on Balance Sheet - Net Change in Cash
        const beginningCashItem = cashRollSection.line_items.find(li => li.key === 'beginning_cash');
        const cashOnBSItem = cashRollSection.line_items.find(li => li.key === 'cash_on_balance_sheet');
        if (beginningCashItem && netChangeItem && cashOnBSItem && 
            beginningCashItem.values.length > periodIdx) {
          const cashOnBS = cashOnBSItem.values[periodIdx];
          const netChange = netChangeItem.values[periodIdx];
          
          if (cashOnBS !== null && netChange !== null) {
            beginningCashItem.values[periodIdx] = cashOnBS - netChange;
          }
        }
        
        // 3. Calculate Ending Cash (Roll-Forward) = Beginning Cash + Net Change in Cash
        const endingCashItem = cashRollSection.line_items.find(li => li.key === 'ending_cash_rollforward');
        if (endingCashItem && beginningCashItem && netChangeItem && 
            endingCashItem.values.length > periodIdx) {
          const beginningCash = beginningCashItem.values[periodIdx];
          const netChange = netChangeItem.values[periodIdx];
          
          if (beginningCash !== null && netChange !== null) {
            endingCashItem.values[periodIdx] = beginningCash + netChange;
          }
        }
        
        // 4. Calculate Check (Ending Cash - Balance Sheet) = Ending Cash Roll-Forward - Cash on Balance Sheet
        const checkItem = cashRollSection.line_items.find(li => li.key === 'check_ending_cash_vs_balance_sheet');
        if (checkItem && endingCashItem && cashOnBSItem && 
            checkItem.values.length > periodIdx) {
          const endingCashRollForward = endingCashItem.values[periodIdx];
          const cashOnBS = cashOnBSItem.values[periodIdx];
          
          if (endingCashRollForward !== null && cashOnBS !== null) {
            checkItem.values[periodIdx] = endingCashRollForward - cashOnBS;
          }
        }
      }
    }
    
    return modifiedData;
  };

  // Load CF data from backend (only when using backend calculations or as fallback)
  useEffect(() => {
    const loadCFData = async () => {
      // Only load backend data if using backend calculations or if frontend data is not available
      if (!selectedProject?.id || (calculationSource === 'frontend' && frontendCFData)) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const reports = await getAllFinancialReports(selectedProject.id);
        console.log('[CF Debug] All reports:', reports);

        if (!reports.cf_detail?.data) {
          // If frontend data is available, don't show error
          if (calculationSource === 'frontend' && frontendCFData) {
            setLoading(false);
            return;
          }
          setError('Cash Flow detail report not found. Please generate reports first.');
          return;
        }

        console.log('[CF Debug] CF detail data:', reports.cf_detail.data);
        setCFDetailData(reports.cf_detail.data);

      } catch (err) {
        console.error('[CF Debug] Error loading CF data:', err);
        // If frontend data is available, don't show error
        if (calculationSource === 'frontend' && frontendCFData) {
          setLoading(false);
          return;
        }
        setError('Failed to load Cash Flow data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadCFData();
  }, [selectedProject?.id, calculationSource, frontendCFData]);

  // Set all sections expanded/collapsed on data load
  useEffect(() => {
    if (currentDataSource) {
      const newExpanded: Record<string, boolean> = {};
      currentDataSource.sections.forEach(section => {
        newExpanded[section.key] = true; // Default: expand all
      });
      setExpanded(newExpanded);
    }
  }, [currentDataSource]);

  // Expand/collapse all logic
  const expandAll = () => {
    if (!currentDataSource) {
      setPendingExpandAll(true);
      return;
    }
    const newExpanded: Record<string, boolean> = {};
    currentDataSource.sections.forEach(section => {
      newExpanded[section.key] = true;
    });
    setExpanded(newExpanded);
    setPendingExpandAll(false);
  };
  const collapseAll = () => {
    if (!currentDataSource) return;
    const newExpanded: Record<string, boolean> = {};
    const lastSectionKey = currentDataSource.sections[currentDataSource.sections.length - 1]?.key;
    currentDataSource.sections.forEach(section => {
      newExpanded[section.key] = section.key === lastSectionKey; // Only cash roll-forward stays expanded
    });
    setExpanded(newExpanded);
  };
  // Auto-expand all after data loads if pendingExpandAll is set
  useEffect(() => {
    if (pendingExpandAll && currentDataSource) {
      expandAll();
    }
  }, [pendingExpandAll, currentDataSource]);

  // Auto-select appropriate view based on project period type
  useEffect(() => {
    if (selectedProject?.period_type) {
      const periodType = selectedProject.period_type;
      const availableViews: ViewType[] = 
        periodType === 'quarterly' ? ['quarterly', 'yearly'] :
        periodType === 'annual' ? ['yearly'] :
        ['monthly', 'quarterly', 'yearly'];
      
      // If current view is not available for this period type, switch to first available
      if (!availableViews.includes(viewType)) {
        setViewType(availableViews[0]);
      }
    }
  }, [selectedProject?.period_type, viewType]);

  // Transform data for view type
  const transformDataForView = (data: CFDetailData | null): CFDetailData | null => {
    if (!data) return null;
    
    // Apply N/A handling for missing opening balances FIRST
    const dataWithNAHandling = applyNAHandling(data);
    
    if (viewType === 'monthly') return dataWithNAHandling;
    const monthlyPeriods = data.periods;
    const allMonths = [...monthlyPeriods].sort(compareMonthYearHelper);
    // Determine target periods and period-to-months mapping
    let targetPeriods: string[] = [];
    let periodToMonths: Record<string, string[]> = {};
    if (viewType === 'quarterly') {
      // Calendar quarters: Q1 = Jan-Mar, Q2 = Apr-Jun, etc.
      const quarters: Record<string, string[]> = {};
      allMonths.forEach(month => {
        const [m, y] = month.split('-');
        let q = '';
        if (['Jan', 'Feb', 'Mar'].includes(m)) q = `Q1'${y}`;
        else if (['Apr', 'May', 'Jun'].includes(m)) q = `Q2'${y}`;
        else if (['Jul', 'Aug', 'Sep'].includes(m)) q = `Q3'${y}`;
        else if (['Oct', 'Nov', 'Dec'].includes(m)) q = `Q4'${y}`;
        if (!quarters[q]) quarters[q] = [];
        quarters[q].push(month);
      });
      targetPeriods = Object.keys(quarters).sort((a, b) => {
        const [qa, ya] = a.split("'");
        const [qb, yb] = b.split("'");
        return ya === yb ? parseInt(qa.slice(1)) - parseInt(qb.slice(1)) : parseInt(ya) - parseInt(yb);
      });
      periodToMonths = quarters;
    } else {
      // Yearly
      const years: Record<string, string[]> = {};
      allMonths.forEach(month => {
        const y = month.split('-')[1];
        if (!years[y]) years[y] = [];
        years[y].push(month);
      });
      targetPeriods = Object.keys(years).sort();
      periodToMonths = years;
    }
    // Identify cash roll-forward section (last section)
    const cashRollKey = data.sections[data.sections.length - 1]?.key;
    // Transform sections
    const transformedSections: CFSection[] = data.sections.map((section, sectionIdx) => {
      const isCashRoll = section.key === cashRollKey;
      return {
        ...section,
        line_items: section.line_items.map(item => {
          let newValues: (number | null)[] = [];
          if (isCashRoll) {
            // Special handling for cash roll-forward lines
            if (item.key === 'beginning_cash') {
              newValues = targetPeriods.map(period => {
                const months = periodToMonths[period] || [];
                if (months.length === 0) return null;
                const firstMonthIdx = monthlyPeriods.indexOf(months[0]);
                return firstMonthIdx !== -1 && item.values ? (item.values[firstMonthIdx] ?? null) : null;
              });
            } else if (item.key === 'ending_cash_rollforward' || item.key === 'cash_on_balance_sheet') {
              newValues = targetPeriods.map(period => {
                const months = periodToMonths[period] || [];
                if (months.length === 0) return null;
                const lastMonthIdx = monthlyPeriods.indexOf(months[months.length - 1]);
                return lastMonthIdx !== -1 && item.values ? (item.values[lastMonthIdx] ?? null) : null;
              });
            } else if (item.key === 'net_change_in_cash') {
              newValues = targetPeriods.map(period => {
                const months = periodToMonths[period] || [];
                return months.reduce((sum, m) => {
                  const idx = monthlyPeriods.indexOf(m);
                  return idx !== -1 && item.values ? sum + (item.values[idx] || 0) : sum;
                }, 0);
              });
            } else {
              // Default: sum values for the period
              newValues = targetPeriods.map(period => {
                const months = periodToMonths[period] || [];
                return months.reduce((sum, m) => {
                  const idx = monthlyPeriods.indexOf(m);
                  return idx !== -1 && item.values ? sum + (item.values[idx] || 0) : sum;
                }, 0);
              });
            }
          } else {
            // Default: sum values for the period
            newValues = targetPeriods.map(period => {
              const months = periodToMonths[period] || [];
              return months.reduce((sum, m) => {
                const idx = monthlyPeriods.indexOf(m);
                return idx !== -1 && item.values ? sum + (item.values[idx] || 0) : sum;
              }, 0);
            });
          }
          return { ...item, values: newValues };
        })
      };
    });
    const transformedData = { periods: targetPeriods, sections: transformedSections };
    
    // Apply cash calculations to the transformed data
    return applyCashCalculations(transformedData);
  };

  const transformedData = transformDataForView(currentDataSource);
  const periods = transformedData?.periods || [];

  console.log('[CF Debug] View type:', viewType);
  console.log('[CF Debug] Calculation source:', calculationSource);
  console.log('[CF Debug] Frontend data available:', !!frontendCFData);
  console.log('[CF Debug] Backend data available:', !!cfDetailData);
  console.log('[CF Debug] Using data source:', currentDataSource === frontendCFData ? 'frontend' : 'backend');
  console.log('[CF Debug] Transformed data periods:', periods);
  console.log('[CF Debug] Original CF data periods:', currentDataSource?.periods);
  console.log('[CF Debug] Transformed data sections:', transformedData?.sections?.map(s => ({ key: s.key, name: s.name })));

  // TTM period info for yearly view
  const getTTMPeriodInfo = () => {
    if (!currentDataSource || viewType !== 'yearly') {
      return { label: 'TTM', range: null };
    }
    
    const projectPeriodType = selectedProject?.period_type || 'monthly';
    const allPeriods = [...transformedData?.periods || []].sort();
    
    let lookbackPeriods: string[];
    switch (projectPeriodType) {
      case 'quarterly':
        // For quarterly data, look back 4 quarters (1 year)
        lookbackPeriods = allPeriods.slice(-4);
        break;
      case 'annual':
        // For annual data, TTM should be the most recent year (closest approximation to trailing twelve months)
        lookbackPeriods = allPeriods.slice(-1);
        break;
      default: // monthly
        // For monthly data, look back 12 months (1 year)
        lookbackPeriods = allPeriods.slice(-12);
        break;
    }
    
    if (lookbackPeriods.length === 0) return { label: 'TTM', range: null };
    
    if (projectPeriodType === 'annual' && lookbackPeriods.length > 1) {
      // For annual data with multiple years, show the range
      const oldestYear = lookbackPeriods[0];
      const newestYear = lookbackPeriods[lookbackPeriods.length - 1];
      return {
        label: 'TTM',
        range: oldestYear === newestYear ? null : `${oldestYear}-${newestYear}`
      };
    } else if (projectPeriodType === 'monthly') {
      // For monthly data, check if it's a partial year
      const allMonths = [...currentDataSource.periods].sort(compareMonthYearHelper);
      const last12Months = allMonths.slice(-12);
      if (last12Months.length === 0) return { label: 'TTM', range: null };
      const oldestMonth = last12Months[0].split('-')[0];
      const newestMonth = last12Months[last12Months.length - 1].split('-')[0];
      const isPartialYear = oldestMonth !== 'Jan' || newestMonth !== 'Dec';
      return {
        label: 'TTM',
        range: isPartialYear ? `${oldestMonth}-${newestMonth}` : null
      };
    } else {
      // For quarterly or single period, no range needed
      return {
        label: 'TTM',
        range: null
      };
    }
  };

  const ttmInfo = getTTMPeriodInfo();

  // TTM calculations for cash flow items - restructured to avoid circular dependencies
  const calculateAllTTMValues = (): Record<string, number | null> => {
    if (!currentDataSource || viewType !== 'yearly') return {};
    
    const allMonths = [...currentDataSource.periods].sort(compareMonthYearHelper);
    const last12Months = allMonths.slice(-12);
    
    if (last12Months.length === 0) return {};
    
    console.log('[TTM Debug] Calculating all TTM values');
    console.log('[TTM Debug] Using data source:', currentDataSource === frontendCFData ? 'frontend' : 'backend');
    console.log('[TTM Debug] Last 12 months:', last12Months);
    console.log('[TTM Debug] Available sections:', currentDataSource.sections.map(s => ({ key: s.key, name: s.name })));
    
    const ttmValues: Record<string, number | null> = {};
    
    // First pass: calculate TTM for all non-section-total items
    currentDataSource.sections.forEach(section => {
      console.log(`[TTM Debug] Processing section: ${section.name} (${section.key})`);
      section.line_items.forEach(item => {
        if (item.key === 'section_total') return; // Skip section totals for now
        
        console.log(`[TTM Debug] Processing line item: ${item.name} (${item.key})`);
        
        if (item.key === 'beginning_cash') {
          // For TTM Beginning Cash with missing opening data, calculate as plug later
          // For now, skip this and calculate after net change is determined
          console.log(`[TTM Debug] Beginning cash TTM will be calculated as plug`);
        } else if (item.key === 'ending_cash_rollforward' || item.key === 'cash_on_balance_sheet') {
          // Ending cash for TTM is the ending cash from the newest month
          const newestMonthIdx = currentDataSource.periods.indexOf(last12Months[last12Months.length - 1]);
          ttmValues[item.key] = newestMonthIdx !== -1 ? (item.values[newestMonthIdx] ?? null) : null;
          console.log(`[TTM Debug] Ending cash TTM: ${ttmValues[item.key]} (from month ${last12Months[last12Months.length - 1]})`);
        } else {
          // For flow items, sum the last 12 months
          const result = last12Months.reduce((sum, month) => {
            const monthIdx = currentDataSource.periods.indexOf(month);
            if (monthIdx !== -1) {
              const monthValue = item.values[monthIdx] || 0;
              return sum + monthValue;
            }
            return sum;
          }, 0);
          ttmValues[item.key] = result;
          console.log(`[TTM Debug] ${item.name} TTM: ${result}`);
        }
      });
    });
    
    // Second pass: calculate section totals from their individual line items
    currentDataSource.sections.forEach(section => {
      const sectionTotal = section.line_items.find(item => item.key === 'section_total');
      if (sectionTotal) {
        console.log(`[TTM Debug] Calculating section total for: ${section.name} (${section.key})`);
        console.log(`[TTM Debug] Section line items:`, section.line_items.map(li => ({ key: li.key, name: li.name })));
        
        const sectionTTMTotal = section.line_items
          .filter(lineItem => lineItem.key !== 'section_total')
          .reduce((sum, lineItem) => {
            const ttmVal = ttmValues[lineItem.key];
            if (ttmVal !== null && ttmVal !== undefined) {
              console.log(`[TTM Debug] Adding ${lineItem.name} (${lineItem.key}) TTM value: ${ttmVal}`);
              return sum + ttmVal;
            } else {
              console.log(`[TTM Debug] Skipping ${lineItem.name} (${lineItem.key}) - no TTM value`);
            }
            return sum;
          }, 0);
        
        // Use a unique key for each section total to avoid overwriting
        const sectionTotalKey = `${section.key}_total`;
        ttmValues[sectionTotalKey] = sectionTTMTotal;
        console.log(`[TTM Debug] Section total ${section.name} TTM: ${sectionTTMTotal} (stored as ${sectionTotalKey})`);
      }
    });
    
    // Third pass: calculate special derived values
    // Net Change in Cash - should already be calculated correctly in first pass, but let's verify
    const netChangeItem = currentDataSource.sections
      .find(section => section.key === 'cash_roll_forward')
      ?.line_items.find(item => item.key === 'net_change_in_cash');

    if (netChangeItem && !ttmValues['net_change_in_cash']) {
      // If not already calculated, calculate it directly from the line item
      const netChangeTTM = last12Months.reduce((sum, month) => {
        const monthIdx = currentDataSource.periods.indexOf(month);
        if (monthIdx !== -1) {
          const monthValue = netChangeItem.values[monthIdx] || 0;
          return sum + monthValue;
        }
        return sum;
      }, 0);
      ttmValues['net_change_in_cash'] = netChangeTTM;
      console.log(`[TTM Debug] Net Change in Cash TTM (direct calculation): ${netChangeTTM}`);
    }
    
    // Recalculate Net Change in Cash as sum of section totals (to match other views)
    const operatingTTM = ttmValues['net_cash_from_operating_activities_total'];
    const investingTTM = ttmValues['net_cash_from_investing_activities_total'];
    const financingTTM = ttmValues['net_cash_from_financing_activities_total'];
    
    if (operatingTTM !== null && investingTTM !== null && financingTTM !== null) {
      const netChangeTTM = operatingTTM + investingTTM + financingTTM;
      ttmValues['net_change_in_cash'] = netChangeTTM;
      console.log(`[TTM Debug] Net Change in Cash TTM (recalculated): ${netChangeTTM} (${operatingTTM} + ${investingTTM} + ${financingTTM})`);
    }
    
    // Calculate Beginning Cash as plug: Cash on Balance Sheet - Net Change in Cash
    const balanceSheetCashTTM = ttmValues['cash_on_balance_sheet'];
    const netChangeTTMFinal = ttmValues['net_change_in_cash'];
    
    if (balanceSheetCashTTM !== null && netChangeTTMFinal !== null) {
      const beginningCashTTM = balanceSheetCashTTM - netChangeTTMFinal;
      ttmValues['beginning_cash'] = beginningCashTTM;
      console.log(`[TTM Debug] Beginning Cash TTM (plug): ${beginningCashTTM} (${balanceSheetCashTTM} - ${netChangeTTMFinal})`);
    }
    
    // Calculate Ending Cash (Roll-Forward) as Beginning Cash + Net Change
    const beginningCashTTMFinal = ttmValues['beginning_cash'];
    const netChangeTTMFinal2 = ttmValues['net_change_in_cash'];
    
    if (beginningCashTTMFinal !== null && netChangeTTMFinal2 !== null) {
      const endingCashRollForwardTTM = beginningCashTTMFinal + netChangeTTMFinal2;
      ttmValues['ending_cash_rollforward'] = endingCashRollForwardTTM;
      console.log(`[TTM Debug] Ending Cash (Roll-Forward) TTM: ${endingCashRollForwardTTM} (${beginningCashTTMFinal} + ${netChangeTTMFinal2})`);
    }
    
    // Check value - for TTM, compare ending cash roll-forward vs balance sheet cash (both should be point-in-time values)
    const endingCashRollForwardTTM = ttmValues['ending_cash_rollforward'];
    
    if (endingCashRollForwardTTM !== null && balanceSheetCashTTM !== null) {
      const checkTTM = endingCashRollForwardTTM - balanceSheetCashTTM;
      ttmValues['check_ending_cash_vs_balance_sheet'] = checkTTM;
      console.log(`[TTM Debug] Check TTM: ${checkTTM} (${endingCashRollForwardTTM} - ${balanceSheetCashTTM})`);
    }
    
    return ttmValues;
  };

  // Get all TTM values
  const allTTMValues = calculateAllTTMValues();
  
  console.log('[TTM Debug] All calculated TTM values:', allTTMValues);
  console.log('[TTM Debug] Section totals:', {
    operating: allTTMValues['net_cash_from_operating_activities_total'],
    investing: allTTMValues['net_cash_from_investing_activities_total'],
    financing: allTTMValues['net_cash_from_financing_activities_total']
  });
  
  // Simple TTM getter function
  const getTTMValue = (itemKey: string): number | null => {
    return allTTMValues[itemKey] ?? null;
  };

  // Helper function to get TTM value for any item, including section totals
  const getItemTTMValue = (item: any): number | null => {
    if (item.key === 'section_total') {
      // Find which section this section_total belongs to
      const section = currentDataSource?.sections.find(section =>
        section.line_items.some(lineItem => lineItem.key === 'section_total' && lineItem.name === item.name)
      );
      
      if (section) {
        const sectionTotalKey = `${section.key}_total`;
        return allTTMValues[sectionTotalKey] ?? null;
      }
    }
    
    return allTTMValues[item.key] ?? null;
  };

  if (!activeTemplate) {
    return <div className="p-8 text-center text-lg text-gray-600">Please select an active template to view the cash flow statement.</div>;
  }
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">Loading Cash Flow data...</p>
      </div>
    );
  }
  if (error && !currentDataSource) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">{error}</p>
        </div>
      </div>
    );
  }
  if (!transformedData) {
    return (
      <div className="p-8 text-center">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">No Cash Flow data available.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-hidden">
      <style>{`
        .financial-table {
          table-layout: auto;
        }
        
        .financial-table th:first-child,
        .financial-table td:first-child {
          width: 240px;
          min-width: 240px;
          max-width: 240px;
        }
        
        .financial-table th:not(:first-child),
        .financial-table td:not(:first-child) {
          width: auto;
          min-width: 160px;
          max-width: 200px;
        }
    
        .financial-table th.value-column {
          width: auto;
          min-width: 160px;
          max-width: 200px;
        }
      `}</style>
      
      {/* Warning Banner for Missing Opening Balances */}
      {hasMissingOpeningBalances() && viewType === 'monthly' && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-amber-800">
                Incomplete Cash Flow Analysis
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                Some cash flow items show "N/A" because opening balance sheet data is unavailable. 
                Section totals include available data only (treating N/A items as $0). 
                Beginning Cash calculated as: Ending Cash - Net Change. 
                Upload a balance sheet for the period before {cfDetailData?.periods[0]} for complete analysis.
              </p>
              <div className="mt-3">
                <button 
                  onClick={() => setShowOpeningBalancesModal(true)}
                  className="text-sm font-medium text-amber-800 hover:text-amber-900 underline"
                >
                  Learn more about opening balances
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partial Period Warning for Quarterly/Yearly Views */}
      {hasMissingOpeningBalances() && (viewType === 'quarterly' || viewType === 'yearly') && (
        <div className="mb-4 p-4 bg-orange-50 border border-orange-200 rounded-lg">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-orange-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-orange-800">
                Partial Period Analysis
              </h3>
              <p className="mt-1 text-sm text-orange-700">
                This {viewType} view includes incomplete data from {cfDetailData?.periods[0]}. 
                Some balance sheet-dependent items are not fully captured, but section totals include all available data. 
                Beginning Cash calculated as: Ending Cash - Net Change. 
                Upload a balance sheet for the period before {cfDetailData?.periods[0]} for complete analysis.
              </p>
              <div className="mt-3">
                <button 
                  onClick={() => setShowOpeningBalancesModal(true)}
                  className="text-sm font-medium text-orange-800 hover:text-orange-900 underline"
                >
                  Learn more about opening balances
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-4 flex justify-end items-center space-x-4">
        <ViewToggle
          currentView={viewType}
          onViewChange={setViewType}
          projectPeriodType={selectedProject?.period_type}
        />
        <button
          onClick={expandAll}
          className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
            // Expand All is active if all sections except cash roll-forward are expanded
            currentDataSource && Object.entries(expanded).filter(([key]) => key !== currentDataSource.sections[currentDataSource.sections.length - 1]?.key).every(([, v]) => v)
              ? 'text-white bg-blue-600 border-transparent'
              : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
          }`}
        >
          <ChevronDownSquare className="h-4 w-4 mr-2" /> Expand All
        </button>
        <button
          onClick={collapseAll}
          className={`inline-flex items-center px-3 py-2 border text-sm leading-4 font-medium rounded-md transition-colors ${
            // Collapse All is active if all sections except cash roll-forward are collapsed
            currentDataSource && Object.entries(expanded).filter(([key]) => key !== currentDataSource.sections[currentDataSource.sections.length - 1]?.key).every(([, v]) => !v)
              ? 'text-white bg-blue-600 border-transparent'
              : 'text-blue-700 bg-blue-100 border-blue-300 hover:bg-blue-200'
          }`}
        >
          <ChevronUpSquare className="h-4 w-4 mr-2" /> Collapse All
        </button>
      </div>
      <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center">
        <span className="mr-2 px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded">Active Template:</span>
        <span className="text-sm text-blue-800 font-medium">{activeTemplate.name}</span>
      </div>
      <div className="overflow-hidden border border-gray-200 rounded-lg">
        <div className="border border-gray-200 rounded-lg">
          <div className="max-h-screen h-128 overflow-auto">
            <div className="inline-block min-w-fit">
              <table className="financial-table divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th className="sticky left-0 top-0 z-[110] px-6 py-3 text-left text-sm font-semibold text-black border-r border-b border-gray-200 bg-gray-50">Description</th>
                    {periods.map(period => (
                      <th key={period} className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50">
                        {viewType === 'yearly' ? `CY ${period}` : getDateForPeriod(period, accounts)}
                      </th>
                    ))}
                    {viewType === 'yearly' && (
                      <th className="sticky top-0 z-[100] px-6 py-3 text-right text-sm font-semibold text-black border-b border-gray-200 value-column bg-gray-50">
                        {ttmInfo.range && (
                          <div className="text-xs text-gray-500 mb-1">{ttmInfo.range}</div>
                        )}
                        {ttmInfo.label}
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {transformedData.sections.map((section, sectionIdx) => {
                    const isCashRoll = sectionIdx === transformedData.sections.length - 1;
                    return (
                      <React.Fragment key={section.key}>
                        <tr className="bg-gray-50 cursor-pointer hover:bg-gray-100" onClick={() => setExpanded(e => ({ ...e, [section.key]: !e[section.key] }))}>
                          <td className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-sm font-semibold text-black border-r border-gray-200">{section.name}</td>
                          {periods.map(period => <td key={`${section.key}-${period}`} />)}
                          {viewType === 'yearly' && <td key={`${section.key}-ttm`} />}
                        </tr>
                        {(expanded[section.key] || isCashRoll) ? (
                          section.line_items
                            .filter(item => {
                              console.log(`[CF Debug] Filtering item: ${item.key} (${item.name})`);
                              console.log(`[CF Debug] Item values:`, item.values);
                              
                              // Always show check/variance lines even if they're zero
                              if (item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check')) {
                                console.log(`[CF Debug] Showing check item: ${item.key}`);
                                return true;
                              }
                              // For yearly view, also show items that might have TTM values or are essential cash roll-forward items
                              if (viewType === 'yearly') {
                                // Always show essential cash roll-forward items in yearly view
                                if (item.key === 'beginning_cash' || item.key === 'ending_cash_rollforward' || item.key === 'cash_on_balance_sheet') {
                                  console.log(`[CF Debug] Showing essential cash roll-forward item in yearly view: ${item.key}`);
                                  return true;
                                }
                                const ttmVal = getItemTTMValue(item);
                                console.log(`[CF Debug] TTM value for ${item.key}:`, ttmVal);
                                if (ttmVal !== null && ttmVal !== undefined && ttmVal !== 0) {
                                  console.log(`[CF Debug] Showing item with TTM value: ${item.key}`);
                                  return true;
                                }
                              }
                              // Show items with non-zero monthly values
                              const hasMonthlyValues = item.values.some(val => val !== null && val !== undefined && val !== 0);
                              console.log(`[CF Debug] Has monthly values: ${hasMonthlyValues}`);
                              return hasMonthlyValues;
                            })
                            .map(item => (
                              <tr key={item.key} className={
                                item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') 
                                  ? 'border-t-2 border-gray-400' 
                                  : item.name.startsWith('Net Cash') 
                                    ? 'bg-gray-100 font-semibold' 
                                    : ''
                              }>
                                <td className={`sticky left-0 z-10 ${item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') ? 'bg-gray-50' : 'bg-white'} px-6 py-${item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') ? '4' : '3'} text-sm ${item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') ? 'font-semibold' : ''} text-black border-r border-gray-200`}>{item.name}</td>
                                {item.values.map((val, i) => {
                                  const isCheckRow = item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check');
                                  const isBalanced = isCheckRow && val !== null && val !== undefined && Math.abs(val) < 0.01;
                                  return (
                                    <td key={`${item.key}-${periods[i]}`} className={`px-6 py-${isCheckRow ? '4' : '2'} text-sm text-right ${
                                      isCheckRow 
                                        ? `font-semibold ${isBalanced ? 'text-green-600' : 'text-red-600'}`
                                        : item.name.startsWith('Net Cash') 
                                          ? 'text-gray-900 font-semibold' 
                                          : 'text-gray-900'
                                    }`}>
                                      {val !== null && val !== undefined ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A'}
                                    </td>
                                  );
                                })}
                                {viewType === 'yearly' && (
                                  <td className={`px-6 py-${item.key === 'check_ending_cash_balance_sheet' || item.name.toLowerCase().includes('check') ? '4' : '2'} text-sm text-right ${
                                    item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') 
                                      ? (() => {
                                          const ttmVal = getItemTTMValue(item);
                                          const isBalanced = ttmVal !== null && ttmVal !== undefined && Math.abs(ttmVal) < 0.01;
                                          return `font-semibold ${isBalanced ? 'text-green-600' : 'text-red-600'}`;
                                        })()
                                      : item.name.startsWith('Net Cash') 
                                        ? 'text-gray-900 font-semibold' 
                                        : 'text-gray-900'
                                  }`}>
                                    {(() => {
                                      const ttmVal = getItemTTMValue(item);
                                      console.log(`TTM for ${item.name} (${item.key}):`, ttmVal);
                                      return ttmVal !== null && ttmVal !== undefined ? ttmVal.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A';
                                    })()}
                                  </td>
                                )}
                              </tr>
                            ))
                        ) : (
                          // If collapsed and not cash roll-forward, only show section total
                          section.line_items
                            .filter(item => {
                              if (item.key === 'section_total') return true;
                              // For yearly view, also show items that might have TTM values or are essential cash roll-forward items
                              if (viewType === 'yearly') {
                                // Always show essential cash roll-forward items in yearly view
                                if (item.key === 'beginning_cash' || item.key === 'ending_cash_rollforward' || item.key === 'cash_on_balance_sheet') {
                                  return true;
                                }
                                const ttmVal = getItemTTMValue(item);
                                if (ttmVal !== null && ttmVal !== undefined && ttmVal !== 0) {
                                  return true;
                                }
                              }
                              return false;
                            })
                            .map(item => (
                              <tr key={item.key} className={
                                item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') 
                                  ? 'border-t-2 border-gray-400' 
                                  : item.name.startsWith('Net Cash') 
                                    ? 'bg-gray-100 font-semibold' 
                                    : ''
                              }>
                                <td className={`sticky left-0 z-10 ${item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') ? 'bg-gray-50' : 'bg-white'} px-6 py-${item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') ? '4' : '3'} text-sm ${item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') ? 'font-semibold' : ''} text-black border-r border-gray-200`}>{item.name}</td>
                                {item.values.map((val, i) => {
                                  const isCheckRow = item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check');
                                  const isBalanced = isCheckRow && val !== null && val !== undefined && Math.abs(val) < 0.01;
                                  return (
                                    <td key={`${item.key}-${periods[i]}`} className={`px-6 py-${isCheckRow ? '4' : '2'} text-sm text-right ${
                                      isCheckRow 
                                        ? `font-semibold ${isBalanced ? 'text-green-600' : 'text-red-600'}`
                                        : item.name.startsWith('Net Cash') 
                                          ? 'text-gray-900 font-semibold' 
                                          : 'text-gray-900'
                                    }`}>
                                      {val !== null && val !== undefined ? val.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A'}
                                    </td>
                                  );
                                })}
                                {viewType === 'yearly' && (
                                  <td className={`px-6 py-${item.key === 'check_ending_cash_balance_sheet' || item.name.toLowerCase().includes('check') ? '4' : '2'} text-sm text-right ${
                                    item.key === 'check_ending_cash_vs_balance_sheet' || item.name.toLowerCase().includes('check') 
                                      ? (() => {
                                          const ttmVal = getItemTTMValue(item);
                                          const isBalanced = ttmVal !== null && ttmVal !== undefined && Math.abs(ttmVal) < 0.01;
                                          return `font-semibold ${isBalanced ? 'text-green-600' : 'text-red-600'}`;
                                        })()
                                      : item.name.startsWith('Net Cash') 
                                        ? 'text-gray-900 font-semibold' 
                                        : 'text-gray-900'
                                  }`}>
                                    {(() => {
                                      const ttmVal = getItemTTMValue(item);
                                      console.log(`TTM for ${item.name} (${item.key}):`, ttmVal);
                                      return ttmVal !== null && ttmVal !== undefined ? ttmVal.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : 'N/A';
                                    })()}
                                  </td>
                                )}
                              </tr>
                            ))
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Opening Balances Information Modal */}
      {showOpeningBalancesModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9999]" onClick={() => setShowOpeningBalancesModal(false)}>
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white" onClick={(e) => e.stopPropagation()}>
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">About Opening Balances</h3>
                <button
                  onClick={() => setShowOpeningBalancesModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="space-y-4 text-sm text-gray-700">
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">Why Opening Balances Matter</h4>
                  <p>
                    Cash flow statements show <strong>changes</strong> in balance sheet accounts between periods. 
                    Without the starting balances (opening balances), we can only see ending balances but cannot calculate the actual changes.
                  </p>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">What's Missing</h4>
                  <ul className="list-disc pl-5 space-y-1">
                    <li><strong>Working Capital Changes:</strong> Changes in receivables, inventory, and payables</li>
                    <li><strong>Capital Expenditures:</strong> Actual spending on property, plant & equipment</li>
                    <li><strong>Cash Roll-Forward:</strong> True beginning cash position and changes</li>
                  </ul>
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 mb-2">How to Fix This</h4>
                  <ol className="list-decimal pl-5 space-y-1">
                    <li>Upload a balance sheet for the period <strong>before</strong> your first month of data</li>
                    <li>For example, if your data starts in January 2024, upload a December 2023 balance sheet</li>
                    <li>The system will automatically calculate proper changes between periods</li>
                  </ol>
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded p-3">
                  <p className="text-blue-800">
                    <strong>Note:</strong> Items showing "N/A" represent incomplete calculations. 
                    Other items (like Net Income and Depreciation from the P&L) are still accurate.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowOpeningBalancesModal(false)}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                >
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

UnifiedDetailedCashFlow.displayName = 'UnifiedDetailedCashFlow';

export default UnifiedDetailedCashFlow;